import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bell, Save } from 'lucide-react';
import { shopApi } from '../../utils/shopApi';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';

export default function NotificationSettingsPage() {
  const [events, setEvents] = useState([]);
  const [labels, setLabels] = useState({});
  const [users, setUsers] = useState([]);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const r = await shopApi.get('/shop/staff/notification-settings');
      setEvents(r.data.events);
      setLabels(r.data.labels);
      setUsers(r.data.users);
      setDirty({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load settings');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = (staffId, eventKey, next) => {
    setUsers((list) =>
      list.map((u) =>
        u.id === staffId ? { ...u, prefs: { ...u.prefs, [eventKey]: next } } : u
      )
    );
    setDirty((d) => ({ ...d, [staffId]: true }));
  };

  const saveUser = async (u) => {
    setSaving(u.id);
    try {
      await shopApi.put(`/shop/staff/${u.id}/notification-settings`, {
        notification_prefs: u.prefs,
      });
      toast.success(`Saved for ${u.name || u.email}`);
      setDirty((d) => {
        const next = { ...d };
        delete next[u.id];
        return next;
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const sendTest = async (u) => {
    try {
      const r = await shopApi.post(`/shop/staff/${u.id}/test-notification`);
      toast.success(`Sent ${r.data.sent} test email(s) to ${r.data.to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test email failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="notification-settings-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Bell className="w-6 h-6 text-primary" />
            <h1 className="text-3xl font-serif font-bold">Email Notifications</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Decide which shop staff receive an email for each order lifecycle event.
            Enabled events require the staff member to have a valid email on file.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-3 text-left">Staff</th>
              {events.map((e) => (
                <th key={e} className="p-3 text-center whitespace-nowrap">
                  {labels[e] || e}
                </th>
              ))}
              <th className="p-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td
                  colSpan={events.length + 2}
                  className="p-6 text-center text-sm text-muted-foreground"
                >
                  No shop staff found.
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr
                key={u.id}
                className="border-t border-border hover:bg-muted/20"
                data-testid={`notif-row-${u.id}`}
              >
                <td className="p-3">
                  <div className="font-medium">
                    {u.name || '—'}
                    {u.active === false && (
                      <span className="text-xs text-muted-foreground ml-2">(inactive)</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {u.email} · <span className="capitalize">{u.role}</span>
                  </div>
                </td>
                {events.map((e) => (
                  <td key={e} className="p-3 text-center">
                    <Switch
                      checked={!!u.prefs?.[e]}
                      onCheckedChange={(v) => toggle(u.id, e, v)}
                      data-testid={`notif-switch-${u.id}-${e}`}
                      disabled={!u.email || u.active === false}
                    />
                  </td>
                ))}
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => sendTest(u)}
                      disabled={!u.email}
                      title="Send test emails for all enabled events"
                      data-testid={`notif-test-${u.id}`}
                    >
                      Test
                    </Button>
                    <Button
                      size="sm"
                      variant={dirty[u.id] ? 'default' : 'outline'}
                      disabled={!dirty[u.id] || saving === u.id}
                      onClick={() => saveUser(u)}
                      data-testid={`notif-save-${u.id}`}
                    >
                      <Save className="w-3.5 h-3.5 mr-1" />
                      {saving === u.id ? 'Saving…' : dirty[u.id] ? 'Save' : 'Saved'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
